import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  Button,
  ContextMenu,
  Input,
  useContextMenu,
  SearchIcon,
  IContextMenuItem,
  ISelectOption,
  Select,
  Pagination,
  Table,
} from '@smartshell/ui-kit';
import { useNavigate } from 'react-router-dom';

import { LayoutContext } from 'components/Layout';
import { useDebouncedValue } from 'hooks';
import { useDeleteCombo } from 'hooks/combo';
import { LocalizationContext } from 'localization';
import { openConfirmDialog } from 'services/Confirmation';

import { FeatureHOC, useIsFeatureAvailable } from '../../features';
import s from './Combos.module.scss';
import { useGetColumns } from './hooks';
import {
  COMBOS,
  HOST_GROUPS,
  TCombosRequest,
  TCombosResponse,
  THostGroupsResponse,
} from './schema';
import { TCombo } from './types';

export const Combos: FC = () => {
  const { t } = useContext(LocalizationContext);
  const layoutElement = useContext(LayoutContext);

  const [searchStr, setSearchStr] = useState('');
  const [hostGroup, setHostGroup] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  const isCombosAvailable = useIsFeatureAvailable('combos');

  useEffect(() => {
    setPage(1);
  }, [searchStr, hostGroup]);

  const variables = useDebouncedValue<TCombosRequest>({
    input: {
      q: searchStr.length > 2 ? searchStr : undefined,
      host_group_id: hostGroup,
    },
    page,
  });

  const navigate = useNavigate();

  const { data, loading, refetch } = useQuery<TCombosResponse, TCombosRequest>(
    COMBOS,
    {
      fetchPolicy: 'cache-and-network',
      variables,
      skip: !isCombosAvailable,
    }
  );
  const { data: hostGroupsResponse } = useQuery<THostGroupsResponse>(
    HOST_GROUPS,
    {
      fetchPolicy: 'cache-and-network',
      skip: !isCombosAvailable,
    }
  );
  const deleteCombo = useDeleteCombo();

  const GROUPS_OPTIONS = useMemo<Array<ISelectOption>>(
    () =>
      hostGroupsResponse?.hostGroupsOverview.map((group) => ({
        title: group.title,
        value: group.id,
      })) ?? [],
    [hostGroupsResponse]
  );

  const contextMenuItems = useMemo<Array<IContextMenuItem<TCombo>>>(
    () => [
      {
        key: 'edit',
        text: t('edit'),
        iconName: 'EditIcon',
        onClick: ({ id }) => {
          navigate(`/combos/${id}`);
        },
      },
      {
        key: 'delete',
        className: s.red,
        text: t('delete'),
        iconName: 'BucketIcon',
        onClick: ({ id, title }) =>
          openConfirmDialog(t, 'remove', {
            title: t('combo.deleting'),
            message: t('combo.deleting.confirm', title),
          }).then(({ status }) => {
            if (status === 'ACCEPTED') {
              return deleteCombo({ variables: { id } }).then(() => refetch());
            }
          }),
      },
    ],
    [t]
  );
  const {
    onContextMenu,
    contextMenuData,
    closeContextMenu,
    cursorPosition,
    onTouchMove,
    onTouchEnd,
    onTouchStart,
  } = useContextMenu<TCombo>();

  const COLUMNS = useGetColumns();

  const lastPage = data?.combos.paginatorInfo.lastPage ?? 1;

  return (
    <FeatureHOC alias="combos" strategy="fade">
      <div className={s.container}>
        {contextMenuData && cursorPosition && (
          <ContextMenu
            layoutElement={layoutElement}
            data={contextMenuData}
            items={contextMenuItems}
            closeContextMenu={closeContextMenu}
            position={cursorPosition}
          />
        )}
        <div className={s.controls}>
          <div className={s.filters}>
            <Input
              variant="secondary"
              value={searchStr}
              onChange={setSearchStr}
              placeholder={t('combos.find.placeholder')}
              leftContent={<SearchIcon color="primary" />}
            />
            <Select
              name="groupFilter"
              value={hostGroup}
              onChange={setHostGroup}
              placeholder={t('hosts.allGroups')}
              options={GROUPS_OPTIONS}
            />
          </div>
          <Button
            onClick={() => {
              navigate('/combos/new', { replace: true });
            }}
            iconName="CirclePlusIcon"
          >
            {t('combo.add.button')}
          </Button>
        </div>
        <div className={s.content}>
          <Table
            data={data?.combos.data ?? []}
            columns={COLUMNS}
            onContextMenu={(event, data) => {
              onContextMenu(event, data);
            }}
            onTouchStart={(event, row) => {
              onTouchStart(event, row);
            }}
            onRowDoubleClick={(event, row) => navigate(`/combos/${row.id}`)}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            isLoading={loading}
            emptyDataPlaceholder={
              isCombosAvailable ? t('combos.placeholder') : ''
            }
            className={s.table}
          />
          {lastPage !== page && (
            <Pagination
              onChange={setPage}
              lastPage={lastPage}
              currentPage={page}
            />
          )}
        </div>
        {/*<Preloader*/}
        {/*  loading={loading}*/}
        {/*  noDataPlaceholder={isCombosAvailable ? t('combos.placeholder') : ''}*/}
        {/*  hasData={!!data?.combos.data.length}*/}
        {/*>*/}
        {/*  <div className={s.list}>*/}
        {/*    {data?.combos.data.map((combo) => (*/}
        {/*      <CombosItem*/}
        {/*        key={`combo-${combo.id}`}*/}
        {/*        combo={combo}*/}
        {/*        onContextMenu={(event) => onContextMenu(event, combo)}*/}
        {/*        onTouchStart={(event) => onTouchStart(event, combo)}*/}
        {/*        onTouchMove={onTouchMove}*/}
        {/*        onTouchEnd={onTouchEnd}*/}
        {/*      />*/}
        {/*    ))}*/}
        {/*  </div>*/}
        {/*  {lastPage !== page && (*/}
        {/*    <Pagination*/}
        {/*      onChange={setPage}*/}
        {/*      lastPage={lastPage}*/}
        {/*      currentPage={page}*/}
        {/*    />*/}
        {/*  )}*/}
        {/*</Preloader>*/}
      </div>
    </FeatureHOC>
  );
};
